import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Plus, Save, Trash2 } from 'lucide-react';
import api from '@/lib/api';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { erroDaApi, type ColunasDaVerba, type ContaDoCentro, type Verba, type Verbas } from './tipos';

type Props = {
  /** Nulo abre o formulário de verba nova. */
  verba: Verba | null;
  centros: Verbas['centros_custo'];
  aoFechar: () => void;
  aoSalvar: () => void;
};

const COLUNAS: { valor: ColunasDaVerba; rotulo: string; detalhe: string }[] = [
  { valor: 'total', rotulo: 'Total', detalhe: 'Salário, bônus e descontos em geral' },
  { valor: 'ativos_outros', rotulo: 'Ativos + afastados', detalhe: 'Proventos variáveis; o de desligados vai para a rescisão' },
];

const campo = 'mt-[2px] h-9 w-full rounded-md border border-border bg-surface px-sm text-[13px] text-ink';

/**
 * A verba como linha da planilha do ERP: o nome que vai no histórico, as contas
 * e, quando algum centro de custo lança em outra conta — a Distribuição Própria
 * usa o grupo 507xx —, a exceção dele.
 */
export default function FormularioVerba({ verba, centros, aoFechar, aoSalvar }: Props) {
  const nova = verba === null;
  const deRubricas = nova || verba.origem === 'rubricas';

  const [nome, setNome] = useState(verba?.nome ?? 'VL ');
  const [debito, setDebito] = useState(verba?.conta_debito ?? '');
  const [credito, setCredito] = useState(verba?.conta_credito ?? '35181');
  const [colunas, setColunas] = useState<ColunasDaVerba>(verba?.colunas === 'ativos_outros' ? 'ativos_outros' : 'total');
  const [contas, setContas] = useState<ContaDoCentro[]>(
    verba?.contas.map(({ centro_custo_id, conta_debito, conta_credito }) => ({ centro_custo_id, conta_debito, conta_credito })) ?? [],
  );

  const salvar = useMutation({
    mutationFn: async () => {
      const dados = {
        nome,
        conta_debito: debito,
        conta_credito: credito,
        ...(deRubricas ? { colunas } : {}),
        contas,
      };

      return nova ? (await api.post('/folha/verbas', dados)).data : (await api.put(`/folha/verbas/${verba.id}`, dados)).data;
    },
    onSuccess: aoSalvar,
  });

  const mudarConta = (indice: number, mudanca: Partial<ContaDoCentro>) =>
    setContas((atual) => atual.map((conta, i) => (i === indice ? { ...conta, ...mudanca } : conta)));

  const ativos = centros.filter((centro) => centro.ativo).length;

  return (
    <Dialog open onOpenChange={(aberto) => !aberto && !salvar.isPending && aoFechar()}>
      <DialogContent className="max-w-[640px] p-lg">
        <DialogTitle>{nova ? 'Nova verba' : `Editar ${verba.nome}`}</DialogTitle>
        <DialogDescription className="mt-xs">
          {nova
            ? `Entra no fim da planilha do ERP com uma linha em cada um dos ${ativos} centros de custo ativos, zerada até receber rubrica.`
            : verba.regra}
        </DialogDescription>

        <form
          onSubmit={(evento) => {
            evento.preventDefault();
            salvar.mutate();
          }}
          className="mt-md space-y-md"
        >
          <label className="block">
            <span className="block text-[12px] font-bold text-muted">Nome no histórico</span>
            <input value={nome} onChange={(evento) => setNome(evento.target.value)} maxLength={60} required className={`${campo} uppercase`} />
          </label>

          <div className="grid grid-cols-2 gap-sm">
            <label className="block">
              <span className="block text-[12px] font-bold text-muted">Conta de débito</span>
              <input value={debito} onChange={(evento) => setDebito(evento.target.value)} maxLength={20} required className={`${campo} tabular-nums`} />
            </label>
            <label className="block">
              <span className="block text-[12px] font-bold text-muted">Conta de crédito</span>
              <input value={credito} onChange={(evento) => setCredito(evento.target.value)} maxLength={20} required className={`${campo} tabular-nums`} />
            </label>
          </div>

          {deRubricas && (
            <fieldset>
              <legend className="text-[12px] font-bold text-muted">Coluna da folha que a verba soma</legend>
              <div className="mt-[2px] grid grid-cols-2 gap-sm">
                {COLUNAS.map((opcao) => (
                  <label
                    key={opcao.valor}
                    className={`flex cursor-pointer gap-sm rounded-md border p-sm ${
                      colunas === opcao.valor ? 'border-accent bg-accent/5' : 'border-border bg-surface'
                    }`}
                  >
                    <input
                      type="radio"
                      name="colunas"
                      checked={colunas === opcao.valor}
                      onChange={() => setColunas(opcao.valor)}
                      className="mt-[3px] accent-accent"
                    />
                    <span>
                      <span className="block text-[13px] font-bold text-ink">{opcao.rotulo}</span>
                      <span className="block text-[12px] text-muted">{opcao.detalhe}</span>
                    </span>
                  </label>
                ))}
              </div>
            </fieldset>
          )}

          <fieldset>
            <legend className="text-[12px] font-bold text-muted">Contas diferentes por centro de custo</legend>
            {contas.length === 0 && <p className="mt-[2px] text-[12px] text-muted">Nenhuma: todo centro de custo usa as contas acima.</p>}

            <ul className="mt-[2px] space-y-xs">
              {contas.map((conta, indice) => (
                <li key={indice} className="flex items-center gap-xs">
                  <select
                    value={conta.centro_custo_id || ''}
                    onChange={(evento) => mudarConta(indice, { centro_custo_id: Number(evento.target.value) })}
                    required
                    aria-label="Centro de custo"
                    className="h-9 min-w-0 flex-1 rounded-md border border-border bg-surface px-sm text-[13px] text-ink"
                  >
                    <option value="">centro de custo…</option>
                    {centros.map((centro) => (
                      <option key={centro.id} value={centro.id}>
                        {centro.nome}
                      </option>
                    ))}
                  </select>
                  <input
                    value={conta.conta_debito}
                    onChange={(evento) => mudarConta(indice, { conta_debito: evento.target.value })}
                    placeholder="débito"
                    aria-label="Conta de débito"
                    maxLength={20}
                    required
                    className="h-9 w-[96px] rounded-md border border-border bg-surface px-sm text-[13px] text-ink tabular-nums"
                  />
                  <input
                    value={conta.conta_credito}
                    onChange={(evento) => mudarConta(indice, { conta_credito: evento.target.value })}
                    placeholder="crédito"
                    aria-label="Conta de crédito"
                    maxLength={20}
                    required
                    className="h-9 w-[96px] rounded-md border border-border bg-surface px-sm text-[13px] text-ink tabular-nums"
                  />
                  <button
                    type="button"
                    title="Tirar a exceção"
                    onClick={() => setContas((atual) => atual.filter((_, i) => i !== indice))}
                    className="rounded-md p-[6px] text-muted hover:bg-danger/10 hover:text-danger"
                  >
                    <Trash2 size={15} />
                  </button>
                </li>
              ))}
            </ul>

            <button
              type="button"
              onClick={() => setContas((atual) => [...atual, { centro_custo_id: 0, conta_debito: debito, conta_credito: credito }])}
              className="mt-xs inline-flex items-center gap-xs rounded-md px-xs py-[4px] text-[13px] font-bold text-accent hover:bg-accent/10"
            >
              <Plus size={14} />
              Acrescentar exceção
            </button>
          </fieldset>

          {salvar.isError && (
            <p className="text-[13px] font-bold text-danger">{erroDaApi(salvar.error, 'Não foi possível salvar a verba.')}</p>
          )}

          <div className="flex justify-end gap-sm">
            <button
              type="button"
              onClick={aoFechar}
              disabled={salvar.isPending}
              className="rounded-md border border-border px-md py-sm text-[13px] font-bold text-ink hover:bg-surface-subtle"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={salvar.isPending}
              className="inline-flex items-center gap-xs rounded-md bg-accent px-md py-sm text-[13px] font-bold text-white hover:bg-accent/90 disabled:opacity-50"
            >
              <Save size={14} aria-hidden="true" />
              {salvar.isPending ? 'Salvando e recalculando…' : nova ? 'Criar verba' : 'Salvar'}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
